import { Badge } from '@components/ui';
import clsx from 'clsx';
import { User, CheckCircle, Clock, AlertCircle } from 'lucide-react';

/**
 * FinApartmentTable — Daire Bazlı Finansal Tablo
 */
export default function FinApartmentTable({ daireler, formatCurrency, filterDurum, searchTerm }) {

    // Filtreleme
    const filteredDaireler = daireler.filter(d => {
        if (filterDurum && d.status !== filterDurum) return false;
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            return d.number.toLowerCase().includes(s) ||
                (d.buyerName && d.buyerName.toLowerCase().includes(s));
        }
        return true;
    });

    if (filteredDaireler.length === 0) {
        return <div className="p-6 text-center text-text-muted text-sm border-t border-border-light bg-bg-tertiary/20">Kriterlere uygun daire bulunamadı.</div>;
    }

    const getStatusInfo = (status) => {
        switch (status) {
            case 'sold': return { label: 'Satıldı', color: 'success', icon: CheckCircle };
            case 'reserved': return { label: 'Rezerve', color: 'warning', icon: Clock };
            case 'available': return { label: 'Boş', color: 'neutral', icon: AlertCircle };
            default: return { label: status, color: 'neutral', icon: AlertCircle };
        }
    };

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
                <thead className="bg-bg-tertiary/50 border-b border-border-light text-xs uppercase text-text-secondary font-semibold">
                    <tr>
                        <th className="px-4 py-3 w-20">No</th>
                        <th className="px-4 py-3">Tip / Kat</th>
                        <th className="px-4 py-3">Durum</th>
                        <th className="px-4 py-3">Alıcı</th>
                        <th className="px-4 py-3 text-right">Satış Bedeli</th>
                        <th className="px-4 py-3 text-right">Tahsilat</th>
                        <th className="px-4 py-3 text-right">Kalan</th>
                        <th className="px-4 py-3 text-center w-24">Ödeme %</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border-light bg-bg-primary">
                    {filteredDaireler.map((daire) => {
                        const status = getStatusInfo(daire.status);
                        const percent = daire.totalPrice > 0 ? Math.round((daire.paidAmount / daire.totalPrice) * 100) : 0;
                        const isSold = daire.status === 'sold' || daire.status === 'reserved';

                        return (
                            <tr key={daire.id} className="hover:bg-bg-hover/50 transition-colors">
                                <td className="px-4 py-3 font-medium text-text-primary">{daire.number}</td>
                                <td className="px-4 py-3 text-text-secondary">
                                    {daire.grossM2}m² • {daire.floor}. Kat
                                </td>
                                <td className="px-4 py-3">
                                    <Badge variant={status.color} size="sm" className="gap-1">
                                        <status.icon size={12} /> {status.label}
                                    </Badge>
                                </td>
                                <td className="px-4 py-3">
                                    {daire.buyerName ? (
                                        <div className="flex items-center gap-1.5 text-text-primary">
                                            <User size={14} className="text-text-muted" />
                                            <span className="truncate max-w-[120px]" title={daire.buyerName}>{daire.buyerName}</span>
                                        </div>
                                    ) : (
                                        <span className="text-text-muted">-</span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-right text-text-primary font-medium">
                                    {isSold ? formatCurrency(daire.totalPrice) : '-'}
                                </td>
                                <td className="px-4 py-3 text-right text-success font-medium">
                                    {isSold ? formatCurrency(daire.paidAmount) : '-'}
                                </td>
                                <td className="px-4 py-3 text-right text-warning font-medium">
                                    {isSold && daire.remainingAmount > 0 ? formatCurrency(daire.remainingAmount) : '-'}
                                </td>
                                <td className="px-4 py-3 content-center">
                                    {isSold ? (
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-12 h-1.5 bg-bg-tertiary rounded-full overflow-hidden">
                                                <div
                                                    className={clsx('h-full', percent === 100 ? 'bg-success' : percent >= 50 ? 'bg-primary' : 'bg-warning')}
                                                    style={{ width: `${percent}%` }}
                                                />
                                            </div>
                                            <span className="text-xs font-medium text-text-muted">%{percent}</span>
                                        </div>
                                    ) : (
                                        <div className="text-center text-text-muted text-xs">-</div>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
